import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/store';
import { fetchDocumentInstance, createDocumentSummary, clearCurrentDocument} from '@/store/slices/documentInstancesSlice';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  TextField,
  Divider,
  Alert,
  Breadcrumbs,
  Link,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Description as DocumentIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import type { DocumentSummary } from '@/types/api';

const ERROR_SCENARIOS = {
  noWorkflow: 'This document does not match any existing workflow. Please check the document metadata or create a new workflow that matches these criteria.',
  processingError: 'An error occurred while processing this document. Some content nodes or quality checks may be incomplete.',
  notFound: 'Document not found. It may have been deleted or you may not have permission to view it.',
  invalidId: 'The Document Instance has not a valid ID'
};

const DocumentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
const { currentItem: document, currentSummary: latestSummary, loading, error } = useSelector((state: RootState) => state.documentInstances);  
  const navigate = useNavigate();
  const [editableSummary, setEditableSummary] = useState<DocumentSummary | null>(null);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const filteredEntries = Object.entries(document?.metadata_json ?? "").filter(([key]) => 
    key.endsWith('Name')
  );
  useEffect(() => {
    if (id === 'error-no-workflow') {
      setErrorState(ERROR_SCENARIOS.noWorkflow);
    } else if (id === 'error-processing') {
      setErrorState(ERROR_SCENARIOS.processingError);
    } else if (id === 'not-found') {
      setErrorState(ERROR_SCENARIOS.notFound);
    }
    if (id) {
    dispatch(clearCurrentDocument());
    const numberId = Number(id);
    dispatch(fetchDocumentInstance(numberId));

    // Logic using 'id' goes here
    } else {
    // Handle the error: ID is missing
    setErrorState(ERROR_SCENARIOS.invalidId);
    }
     
  }, [id, dispatch]);


  // Load the LAST summary into the state
  useEffect(() => {
    if (latestSummary) {
      setEditableSummary(latestSummary);
    }
  }, [document, latestSummary]); // relying on the helper variable derived from document

  // Save Logic to replace the LAST item
  const handleSaveSummary = async () => {
    if (!document || !editableSummary) return;

    try {

       await dispatch(createDocumentSummary({ 
        document_instance_id: document.id, 
        content: editableSummary.content 
      })).unwrap(); 

      setIsEditingSummary(false);
      console.log('Summary saved successfully');
    } catch (err) {
      console.error("Failed to save:", err);
    }
  };

  const handleBackToDocuments = () => {
    navigate('/documents');
  };

  const formatFieldName = (key: string): string => {
    switch (key) {
      case 'proFormaName':
        return 'Pro Forma';
      case 'documentTypeName':
        return 'Document Type';
      case 'territoryName':
        return 'Territory';
      default:
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
    }
  };
  if (loading ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  if (errorState || error) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={handleBackToDocuments}
            sx={{ textDecoration: 'none', cursor: 'pointer' }}
          >
            Documents
          </Link>
          <Typography color="text.primary">Document View</Typography>
        </Breadcrumbs>

        <Alert severity="error" sx={{ mb: 3 }}>
          {error ? error : errorState}
        </Alert>

        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToDocuments}
        >
          Back to Documents
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={handleBackToDocuments}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Typography color="text.primary">Document View</Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToDocuments}
        sx={{ mb: 3 }}
      >
        Back to Documents
      </Button>

      {/* Document Settings Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <DocumentIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight="bold">
            Document Settings
          </Typography>
        </Box>

        {/* File Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            FILE NAME
          </Typography>
          <Typography variant="h6" fontWeight="medium">
            {document?.filename}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Document Metadata */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
          DOCUMENT METADATA
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {filteredEntries.map(([key, value]) => (
            <Box key={key} sx={{ minWidth: 200 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {formatFieldName(key)}
              </Typography>
              <Chip
                label={String(value)}
                variant="outlined"
                size="small"
                sx={{ mt: 0.5, display: 'block', width: 'fit-content' }}
              />
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Content Node Output Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          Content Generation Flow
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {document?.workflow_runs?.[0]?.step_objects
    ?.filter(step => step.node_type === 'CONTENT')
    .sort((a, b) => (a.node_sequence_order ?? 0) - (b.node_sequence_order ?? 0))
    .map((node, index, array) => (
      <React.Fragment key={node.id}>
        {/* The Content Card */}
        <Card variant="outlined">
          <CardContent>
            {/* Right-aligned prompt */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  width: '80%',
                  fontWeight: 'medium',
                  textAlign: 'right',
                  color: 'text.secondary'
                }}
              >
                {node.node_prompt}
              </Typography>
            </Box>
            
            {/* Left-aligned output */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  width: '80%',
                  lineHeight: 1.6
                }}
              >
                {node.output}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* The Arrow (Only render if NOT the last item) */}
        {index < array.length - 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ArrowDownwardIcon color="action" />
          </Box>
        )}
      </React.Fragment>
    ))}
        </Box>
      </Paper>

      {/* Quality Check Results Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          Quality Check Results
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {document?.workflow_runs?.[0]?.step_objects?.filter(step => step.node_type === 'QC').map((check) => (
            <Card key={check.id} variant="outlined">
              <CardContent>
                {/* Right-aligned prompt */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      width: '80%',
                      fontWeight: 'medium',
                      textAlign: 'right',
                      fontStyle: 'italic',
                      color: 'text.secondary'
                    }}
                  >
                    {check.node_prompt}
                  </Typography>
                </Box>
                
                {/* Left-aligned output */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      width: '80%',
                      lineHeight: 1.6
                    }}
                  >
                    {check.output}
                  </Typography>
                </Box>

                {check.node_pass_fail_prompt && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 1,
                      backgroundColor: check.pass_fail_output === 'PASS' ? 'success.50' : 'error.50',
                      border: 1,
                      borderColor: check.pass_fail_output === 'PASS' ? 'success.main' : 'error.main'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            PASS/FAIL CHECK
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {check.node_pass_fail_prompt}
                          </Typography>
                        </Box>
                        <Chip
                          icon={check.pass_fail_output === 'PASS' ? <CheckCircleIcon /> : <CancelIcon />}
                          label={check.pass_fail_output}
                          color={check.pass_fail_output === 'PASS' ? 'success' : 'error'}
                          sx={{ ml: 2 }}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>

      {/* Summary Editor Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            Document Summary
          </Typography>
          <Box>
            {isEditingSummary ? (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  onClick={() => {
                    setEditableSummary(latestSummary);
                    setIsEditingSummary(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSummary}
                >
                  Save
                </Button>
              </Stack>
            ) : (
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setIsEditingSummary(true)}
              >
                Edit Summary
              </Button>
            )}
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
        Last modified: {
        latestSummary?.created_at 
              ? new Date(latestSummary.created_at).toLocaleString() 
              : 'N/A'
        }        
        </Typography>

        {isEditingSummary ? (
          <TextField
            key={latestSummary ? `summary-${document?.id}-${latestSummary.created_at}` : 'new-summary'}
            multiline
            fullWidth
            minRows={8}
            maxRows={16}
            // 1. EXTRACT: Read the string from inside the object
            value={editableSummary?.content || ''} 
            // 2. UPDATE: Keep the old object props, just change the content
            onChange={(e) => setEditableSummary(prev => 
              prev ? { ...prev, content: e.target.value } : null
            )}
            variant="outlined"
            placeholder="Enter document summary..."
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }
            }}
            slotProps={{
              inputLabel: { 
                shrink: true 
              }
            }}
          />
        ) : (
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              backgroundColor: 'grey.50',
              minHeight: 200,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
            onClick={() => setIsEditingSummary(true)}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-line',
                lineHeight: 1.6,
                fontFamily: 'inherit'
              }}
            >
            {latestSummary?.content || 'No summary available.'}
            </Typography>
          </Paper>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          * Changes are automatically saved. Edit history is maintained for prompt improvement analysis.
        </Typography>
      </Paper>
    </Box>
  );
};

export default DocumentView;
